import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { accessLevelResponse } from 'src/app/shared/constants/response-mapping';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { ISharedData } from 'src/app/shared/state-management/state-interfaces';
import {
  convertParseDateTime,
  extractDateTimeFromDateString,
} from 'src/app/shared/utils/utility.service';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import {
  IAccessDetail,
  IUserProfile,
} from 'src/app/user-management/user-management-interfaces';
import { AddUserComponent } from '../../add-user/add-user.component';
import { HelperService } from 'src/app/shared/utils/helper.service';

@Component({
  selector: 'app-user-profile-access',
  templateUrl: './user-profile-access.component.html',
  styleUrls: ['./user-profile-access.component.scss'],
})
export class UserProfileAccessComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() dialogRef?: any; //close event of parent component
  @Input() stepper!: number;
  @Input() profileDetails!: IUserProfile;
  @Input() ownProfile!: boolean;
  @Output() stepperChange = new EventEmitter<number>();
  @Output() addNewAccess = new EventEmitter<void>();
  @Input() assigningAccess = false;
  @Output() removeUserAccessHandler = new EventEmitter<string[]>();
  @Input() userDetailsFormGroupName!: string;
  @Output() selectedAccessChange = new EventEmitter<any>();
  @Input() selectedAccess!: any;
  @Input() accessTimeFormGroupName!: string;
  @Input() userId!: string;
  userAccessOperations: 'EDIT' | 'REMOVE' = 'EDIT';
  @Input() accessIds: string[] = [];
  @Input() flowType!: string;
  @Output() flowTypeChange = new EventEmitter<string>();

  commonAccessControl!: boolean;
  userType!: string | undefined;
  formGroupUserDetail!: FormGroup;
  readonly LANG_CONST = LANG_CONST;
  readonly accessLevelResponse = accessLevelResponse;
  readonly CONSTANTS = CONSTANTS;
  userAccess!: any;
  formGroup: any;
  userAccessformGroup!: FormGroup;
  shareedDataSubscription?: Subscription;
  sharedData?: ISharedData;
  siteTimeZone: string | undefined;

  /**
   * @description control getter formgroup
   * @returns rective controls
   */
  get formInstance() {
    return this.formGroup.controls;
  }

  constructor(
    private rootFormGroup: FormGroupDirective,
    public sharedDataService: SharedDataService,
    private helperService: HelperService
  ) {}

  ngOnInit(): void {
    this.shareedDataSubscription = this.sharedDataService.currentData.subscribe(
      (sharedData) => {
        this.sharedData = sharedData;
        const currentSiteId = this.sharedData?.selectedSite?.siteId;
        this.siteTimeZone = <string>this.sharedData?.selectedSite?.siteTimeZone;
        this.commonAccessControl = sharedData.selectedSite.commonAccessControl;
      }
    );
    this.userAccess = this.profileDetails?.userAccess;
    this.userAccess = {
      homeAccess: [
        ...this.initializeSelectionList(
          this.userAccess?.homeAccess,
          CONSTANTS?.HOME_ACCESS
        ),
      ],
      otherAccess: [
        ...this.initializeSelectionList(
          this.userAccess?.otherAccess,
          CONSTANTS.OTHER_ACCESS
        ),
      ],
      temporaryAccess: [
        ...this.initializeSelectionList(
          this.userAccess?.temporaryAccess,
          CONSTANTS.TEMPORARY_ACCESS
        ),
      ],
    };
    this.userType = this.profileDetails?.userType;
    this.formGroup = this.rootFormGroup.control.get(
      this.userDetailsFormGroupName
    ) as FormGroup;
    this.userAccessformGroup = this.rootFormGroup.control.get(
      this.accessTimeFormGroupName
    ) as FormGroup;
  }

  /**
   * @description close modal dialogue with help of material service
   * @returns void
   */
  closeDialog(): void {
    this.dialogRef?.close();
  }

  /**
   * @description navigate to back screen
   * @returns void
   */
  navigateBack(): void {
    if (this.userAccessOperations === CONSTANTS.REMOVE) {
      this.resetState();
      this.userAccessOperations = CONSTANTS.EDIT as 'EDIT';
    } else {
      this.stepperChange.emit((this.stepper = 1));
    }
  }

  /**
   * @description This function set the state false in homeAccess, otherAccess, temporaryAccess object and set an empty accessIds key for disabled remove state button
   * @returns void
   */
  resetState() {
    this.accessIds = [];
    this.userAccess = {
      homeAccess: this.userAccess?.homeAccess?.map((accessObject: any) => ({
        ...accessObject,
        state: false,
      })),
      otherAccess: this.userAccess?.otherAccess?.map((accessObject: any) => ({
        ...accessObject,
        state: false,
      })),
      temporaryAccess: this.userAccess?.temporaryAccess?.map(
        (accessObject: any) => ({ ...accessObject, state: false })
      ),
    };
  }

  /**
   * @description removeAccessHandler emits the updated remove list to the parent component.
   * @returns void
   */

  removeAccessHandler(event: any) {
    this.flowTypeChange.emit(CONSTANTS.REMOVE_USER_ACCESS);
    this.removeUserAccessHandler.emit(this.accessIds);
  }

  /**
   * InitializeSelectionList
   * Initializes a selection list for user access based on the given key.
   *
   * @param userAccess - The list of user access to be initialized.
   * @param key - The key representing the type of user access (e.g., home, other, temporary).
   * @returns The initialized user access list.
   */
  initializeSelectionList(
    userAccess: IAccessDetail[],
    key: string
  ): IAccessDetail[] {
    return userAccess?.map((access) => {
      return { ...access, state: false, type: key };
    });
  }

  /**
   * ngOnChanges
   * Lifecycle hook called when input properties change.
   *
   * @param changes - Object containing each changed property.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.profileDetails && changes.profileDetails.currentValue) {
      this.userAccess = changes.profileDetails?.currentValue?.userAccess;
      this.userAccess = {
        homeAccess: [
          ...this.initializeSelectionList(
            this.userAccess.homeAccess,
            CONSTANTS.HOME_ACCESS
          ),
        ],
        otherAccess: [
          ...this.initializeSelectionList(
            this.userAccess.otherAccess,
            CONSTANTS.OTHER_ACCESS
          ),
        ],
        temporaryAccess: [
          ...this.initializeSelectionList(
            this.userAccess.temporaryAccess,
            CONSTANTS.TEMPORARY_ACCESS
          ),
        ],
      };
    }
  }

  /**
   * @description add new control for form control
   * @param controlName control name for creation
   * @returns void
   */
  addNewControl(controlName: string, required = false): void {
    let dCtrl = new FormControl();
    if (required) {
      dCtrl.setValidators(MFValidators.required);
    }
    this.userAccessformGroup.addControl(controlName, dCtrl);
    dCtrl.updateValueAndValidity();
  }

  editAccessEmitter(access: any) {
    this.userAccessformGroup.reset();
    this.selectedAccessChange.emit((this.selectedAccess = access));

    if (access.repeatDays && access.repeatDays!.length > 0) {
      this.userAccessformGroup?.controls?.startTime?.patchValue('');
      this.userAccessformGroup?.controls?.expireTime?.patchValue('');
      this.addNewControl('repeatStartTime');
      this.addNewControl('repeatEndTime');
      this.addNewControl('repeatDays', true);
      this.userAccessformGroup?.patchValue({
        repeat: true,
        repeatDays: access.repeatDays,
        repeatStartTime: convertParseDateTime(
          access.repeatStartTime,
          'HH:mm',
          'hh:mm a'
        ),
        repeatEndTime: convertParseDateTime(
          access.repeatEndTime,
          'HH:mm',
          'hh:mm a'
        ),
      });
      this.userAccessformGroup?.controls?.repeatStartTime?.markAsTouched();
      this.userAccessformGroup?.controls?.repeatEndTime?.markAsTouched();
      this.userAccessformGroup?.controls?.repeatDays?.markAsTouched();
    } else {
      this.userAccessformGroup?.patchValue({
        repeat: false,
        startTime: convertParseDateTime(
          extractDateTimeFromDateString(String(access.startTime)),
          "yyyy-MM-dd'T'HH:mm:ss",
          "EEE, MMM do, yyyy 'at' h:mm a"
        ),
        expireTime: convertParseDateTime(
          extractDateTimeFromDateString(String(access.expireTime)),
          "yyyy-MM-dd'T'HH:mm:ss",
          "EEE, MMM do, yyyy 'at' h:mm a"
        ),
      });
    }

    this.stepperChange.emit((this.stepper = 8));
  }

  /**
   * onSettings
   * Sets the userAccessOperations property to 'REMOVE' when the settings are invoked.
   */
  onSettings() {
    this.userAccessOperations = CONSTANTS.REMOVE as 'REMOVE';
  }

  /**
   * removeAccessList
   * Updates the component's accessIds property.
   *
   * @param accessIds - The list of access identifiers to be logged and updated.
   */
  removeAccessList(accessIds: string[]) {
    this.accessIds = [...accessIds];
  }

  redirectToShareAccess() {
    const dialogInstance = this.helperService.openModalDialog(
      AddUserComponent,
      {
        accessSharing: true,
        isEditable: false,
        enableBackOperation: false,
        defaultNavigation: CONSTANTS.BUILDING,
        siteTimeZone: this.siteTimeZone,
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
  }

  ngOnDestroy(): void {
    this.shareedDataSubscription?.unsubscribe();
  }
}
